import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { queueCount, syncAll, onQueueChange } from "@/lib/offlineQueue";

const Ctx = createContext(null);

export function OfflineSyncProvider({ children }) {
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(async () => { setPending(await queueCount()); }, []);

  const syncNow = useCallback(async () => {
    if (!navigator.onLine) return { synced: 0, failed: 0 };
    setSyncing(true);
    let r = { synced: 0, failed: 0 };
    try { r = await syncAll(); } finally { setSyncing(false); }
    await refresh();
    if (r.synced > 0) toast.success(`${r.synced} registro(s) sincronizado(s) correctamente`);
    if (r.failed > 0) toast.error(`${r.failed} registro(s) no se pudieron sincronizar`);
    return r;
  }, [refresh]);

  useEffect(() => {
    refresh();
    const unsub = onQueueChange(refresh);
    const goOnline = () => { setOnline(true); syncNow(); };
    const goOffline = () => setOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    if (navigator.onLine) syncNow();
    return () => {
      unsub();
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [refresh, syncNow]);

  return (
    <Ctx.Provider value={{ online, pending, syncing, syncNow, refresh }}>
      {children}
    </Ctx.Provider>
  );
}

export const useOfflineSync = () => useContext(Ctx);
